import React, { useState, useEffect, useRef, useCallback } from "react";
import { PropTypes } from "prop-types";
import reactWrapper from "@harbor/elements/utils/react/wrapper";
import { useI18n } from "../../../i18n/useI18n";
import Spinner from "../../../common/Spinner";
import StackedMultiLineChart from "../../../common/StackedMultiLineChart";
import CircuitsTableView from "../circuitsTable";
import apiService from "../../../config/api-config";
import { chartPrimaryColors } from "../../../utils/colors";
import { setChartDataReport, chartTypes } from "../bandwidth/util";
import { getTimestampFromUTCString } from "../../../utils/displayTime";
import { REPORT_NAMES } from "../../../utils/enums";
const [HbrCard] = reactWrapper(["hbr-card"]);

const chartHeight = "250px";

const View = ({ globalV4PayloadReport, siteNameSet, min, max }) => {
  const { tViews } = useI18n("views");
  const [tx_bps, setTx_bps] = useState([]);
  const [total_bps, setTotal_bps] = useState([]);
  const [rx_bps, setRx_bps] = useState([]);
  const [bandwidthData, setBandwidthData] = useState([]);
  const [circuitData, setCircuitData] = useState([]);
  const [loader, setLoader] = useState(true);
  const [loaderUtilization, setLoaderUtilization] = useState(true);
  const [selections, setSelections] = useState({});
  const [utilReportLoaded, setUtilReportLoaded] = useState(false);
  const [tableLoaded, setTableLoaded] = useState(false);
  const [chartUtil, setChartUtil] = useState(false);
  const [txUtil, setTxUtil] = useState([]);
  const [rxUtil, setRxUtil] = useState([]);
  const [isSetAllSites, setAllSites] = useState(false);
  const [reportType, setReportType] = useState(
    REPORT_NAMES.REPORT_LINK_UTILIZATION,
  );
  const networkCharts = [
    {
      title: tViews("circuit.ctRxTx"),
      valueY: total_bps,
      unit: tViews("circuit.bandwidthRxTx"),
    },
    {
      title: tViews("circuit.ctTx"),
      valueY: tx_bps,
      unit: tViews("circuit.bandwidthTx"),
    },
    {
      title: tViews("circuit.ctRx"),
      valueY: rx_bps,
      unit: tViews("circuit.bandwidthRx"),
    },
    {
      title: tViews("circuit.linkUtilTxUtilizationTitle"),
      valueY: txUtil,
      unit: tViews("txUtilizationWithPercentage"),
    },
    {
      title: tViews("circuit.linkUtilRxUtilizationTitle"),
      valueY: rxUtil,
      unit: tViews("rxUtilizationWithPercentage"),
    },
  ];
  const previewRef = useRef(null);
  const getAppData = useCallback(async (payload) => {
    setLoader(true);
    const result = await apiService.getLinkUtilizationReportWidget(payload);
    const reportRows = result?.data?.data;
    const entryDataTX = {};
    const entryDataTotalBPS = {};
    const entryDataRX = {};
    const visibleCircuits = {};
    if (reportRows?.length > 0) {
      for (const {
        circuit_display_name,
        entry_ts,
        tx_bps,
        total_bps,
        rx_bps,
        bandwidth_rank,
      } of reportRows) {
        setBandwidthData(reportRows);
        const date = getTimestampFromUTCString(entry_ts);
        visibleCircuits[circuit_display_name] = bandwidth_rank;
        if (Number.isFinite(tx_bps)) {
          if (entryDataTX[date] instanceof Object)
            entryDataTX[date][circuit_display_name] = tx_bps;
          else entryDataTX[date] = { date, [circuit_display_name]: tx_bps };
        }
        if (Number.isFinite(total_bps)) {
          if (entryDataTotalBPS[date] instanceof Object)
            entryDataTotalBPS[date][circuit_display_name] = total_bps;
          else
            entryDataTotalBPS[date] = {
              date,
              [circuit_display_name]: total_bps,
            };
        }
        if (Number.isFinite(rx_bps)) {
          if (entryDataRX[date] instanceof Object)
            entryDataRX[date][circuit_display_name] = rx_bps;
          else entryDataRX[date] = { date, [circuit_display_name]: rx_bps };
        }
      }
      let selections = Object.keys(visibleCircuits);
      let bandwidthColor = {};
      selections.forEach((item, appIndex) => {
        bandwidthColor[item] = chartPrimaryColors[appIndex];
      });
      setSelections(bandwidthColor);
      setTx_bps(Object.values(entryDataTX));
      setTotal_bps(Object.values(entryDataTotalBPS));
      setRx_bps(Object.values(entryDataRX));
      setLoader(false);
      setUtilReportLoaded(true);
    } else {
      setLoader(false);
      setUtilReportLoaded(true);
    }
  }, []);

  const isreportCallback = (data) => {
    setTableLoaded(data);
  };

  const selectedReportData = (circuitData) => {
    setCircuitData(circuitData);
  };

  useEffect(() => {
    if (utilReportLoaded) {
      if (
        circuitData &&
        circuitData.length > 0 &&
        bandwidthData &&
        bandwidthData.length > 0
      ) {
        setLoaderUtilization(true);
        let result = setChartDataReport(bandwidthData, circuitData);
        const visibleCircuits = {};
        const entryDataTXUtil = {};
        const entryDataRXUtil = {};
        if (result.length > 0) {
          for (const {
            rx,
            tx,
            circuit_display_name,
            entry_ts,
            bandwidth_rank,
          } of result) {
            const date = getTimestampFromUTCString(entry_ts);
            visibleCircuits[circuit_display_name] = bandwidth_rank;
            if (Number.isFinite(rx)) {
              if (entryDataTXUtil[date] instanceof Object)
                entryDataTXUtil[date][circuit_display_name] = rx;
              else entryDataTXUtil[date] = { date, [circuit_display_name]: rx };
            }
            if (Number.isFinite(tx)) {
              if (entryDataRXUtil[date] instanceof Object)
                entryDataRXUtil[date][circuit_display_name] = tx;
              else entryDataRXUtil[date] = { date, [circuit_display_name]: tx };
            }
          }
          let selections = Object.keys(visibleCircuits);
          let bandwidthColor = {};
          selections.forEach((item, appIndex) => {
            bandwidthColor[item] = chartPrimaryColors[appIndex];
          });
          if (
            Object.keys(entryDataTXUtil).length > 0 ||
            Object.keys(entryDataRXUtil).length > 0
          ) {
            setReportType(REPORT_NAMES.REPORT_LINK_UTILIZATION_BANDWIDTH);
          }
          setSelections(bandwidthColor);
          setTxUtil(Object.values(entryDataTXUtil));
          setRxUtil(Object.values(entryDataRXUtil));
          setLoaderUtilization(false);
          setChartUtil(true);
        } else {
          setTxUtil([]);
          setRxUtil([]);
          setLoaderUtilization(false);
          setChartUtil(true);
        }
      } else {
        setTxUtil([]);
        setRxUtil([]);
        setLoaderUtilization(false);
        setChartUtil(true);
      }
    }
  }, [bandwidthData, circuitData, utilReportLoaded]);
  useEffect(() => {
    if (
      utilReportLoaded &&
      tableLoaded &&
      chartUtil &&
      previewRef.current &&
      (siteNameSet || isSetAllSites)
    ) {
      previewRef.current.id = "isLoaded_chart";
    }
  }, [utilReportLoaded, tableLoaded, chartUtil, siteNameSet, isSetAllSites]);

  useEffect(() => {
    let payload = {
      ...globalV4PayloadReport,
      sort: {
        entry_ts: "asc",
        bandwidth_rank: "asc",
      },
      bandwidth_rank: [1, 2, 3, 4, 5],
    };
    getAppData(payload);
    if (globalV4PayloadReport["site_id"] == undefined) {
      setAllSites(true);
    }
  }, []);
  return (
    <div className="report-content" ref={previewRef}>
      <div className="hbr-css__layout-col-lg">
        <HbrCard className="widget-container" container>
          <div className="flex-column flex-main-content hbr-css__layout-col">
            <div className="title-block">
              <div className="hbr-type-h2">{networkCharts[0].title}</div>
            </div>
            {loader === true ? (
              <Spinner />
            ) : (
              <StackedMultiLineChart
                id={networkCharts[0].title}
                data={networkCharts[0].valueY}
                areaChart={false}
                colorset={selections}
                reporting={true}
                reportingChartType={chartTypes[0].value}
                label={networkCharts[0].unit}
                stacked={false}
                height={chartHeight}
                min={min}
                max={max}
              />
            )}
          </div>
        </HbrCard>

        <HbrCard className="widget-container" container>
          <div className="flex-column flex-main-content hbr-css__layout-col">
            <div className="title-block">
              <div className="hbr-type-h2">{networkCharts[1].title}</div>
            </div>
            {loader === true ? (
              <Spinner />
            ) : (
              <StackedMultiLineChart
                id={networkCharts[1].title}
                data={networkCharts[1].valueY}
                areaChart={false}
                colorset={selections}
                label={networkCharts[1].unit}
                stacked={false}
                height={chartHeight}
                reporting={true}
                reportingChartType={chartTypes[0].value}
                min={min}
                max={max}
              />
            )}
          </div>
        </HbrCard>

        <HbrCard className="widget-container" container>
          <div className="flex-column flex-main-content hbr-css__layout-col">
            <div className="title-block">
              <div className="hbr-type-h2">{networkCharts[2].title}</div>
            </div>
            {loader === true ? (
              <Spinner />
            ) : (
              <StackedMultiLineChart
                id={networkCharts[2].title}
                data={networkCharts[2].valueY}
                areaChart={false}
                colorset={selections}
                label={networkCharts[2].unit}
                stacked={false}
                height={chartHeight}
                reporting={true}
                reportingChartType={chartTypes[0].value}
                min={min}
                max={max}
              />
            )}
          </div>
        </HbrCard>

        {networkCharts &&
          networkCharts[3] &&
          networkCharts[3].valueY &&
          networkCharts[3].valueY.length > 0 && (
            <HbrCard className="widget-container" container>
              <div className="flex-column flex-main-content hbr-css__layout-col">
                <div className="title-block">
                  <div className="hbr-type-h2">{networkCharts[3].title}</div>
                </div>
                {loaderUtilization === true ? (
                  <Spinner />
                ) : (
                  <StackedMultiLineChart
                    id={networkCharts[3].title}
                    data={networkCharts[3].valueY}
                    areaChart={false}
                    colorset={selections}
                    label={networkCharts[3].unit}
                    reporting={true}
                    stacked={false}
                    height={chartHeight}
                    min={min}
                    max={max}
                  />
                )}
              </div>
            </HbrCard>
          )}

        {networkCharts &&
          networkCharts[4] &&
          networkCharts[4].valueY &&
          networkCharts[4].valueY.length > 0 && (
            <HbrCard className="widget-container" container>
              <div className="flex-column flex-main-content hbr-css__layout-col">
                <div className="title-block">
                  <div className="hbr-type-h2">{networkCharts[4].title}</div>
                </div>
                {loaderUtilization === true ? (
                  <Spinner />
                ) : (
                  <StackedMultiLineChart
                    id={networkCharts[4].title}
                    data={networkCharts[4].valueY}
                    areaChart={false}
                    colorset={selections}
                    reporting={true}
                    label={networkCharts[4].unit}
                    stacked={false}
                    height={chartHeight}
                    min={min}
                    max={max}
                  />
                )}
              </div>
            </HbrCard>
          )}

        <CircuitsTableView
          globalV4PayloadReport={globalV4PayloadReport}
          reportName={reportType}
          isreportCallback={isreportCallback}
          selectedReportData={selectedReportData}
        />
      </div>
    </div>
  );
};

View.propTypes = {
  globalV4PayloadReport: PropTypes.object.isRequired,
  siteNameSet: PropTypes.bool,
  min: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  max: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default View;
